import * as R from 'ramda';
import { compareAsc, format } from 'date-fns/fp';

import { capitalize } from './general';


/**
 * sortRequests :: [Request] -> [Request]
 */
export const sortRequests = R.sort((a, b) => compareAsc(a.requestDate, b.requestDate));


/**
 * groupRequestsByStatuses :: [Request] -> [Request]
 */
export const groupRequestsByStatuses = (requests, requestsStatuses) => {
  const state = Object.values(requestsStatuses);
  const titles = ['Inbox', 'Contacted', 'Needs analyzed and assessed', 'Qualified', 'Long-term'];

  const statusesIds = titles.reduce((acc, item) => {
    const status = state.find(({ title }) => title === item);

    if (status) acc.push(status._id);

    return acc;
  }, []);

  return statusesIds.reduce((acc, status) => {
    const req = requests.filter(i => i.status._id === status);

    if (req) acc.push({ [status]: req });

    return acc;
  }, []);
};


/**
 * checkIsSearchingOrFilter :: (String, String) -> Boolean
 */
export const checkIsSearchingOrFilter = (term = '', filters) =>
  (!!term && term.length > 1)
  || !R.isNil(filters.statuses[0])
  || !R.isNil(filters.sources[0])
  || !R.isNil(filters.labels[0])
  || !R.isNil(filters.assignees[0])
  || !R.isNil(filters.dateRange[0])
  || filters.onlyFromCompanies;


/**
 * groupRequestsByMonth :: [Request] -> Object
 */
export const groupRequestsByMonth = R.compose(
  R.defaultTo({}),
  R.groupBy(R.compose(format('MM/yy'), R.prop('requestDate'))),
  sortRequests,
  R.values,
);


/**
 * requestSettingToOptions :: Object -> [Object]
 */
export const requestSettingToOptions = R.compose(
  R.map(({
    _id, title, color, description = '',
  }) => ({
    _id,
    value: capitalize(title),
    label: capitalize(title),
    description,
    color,
  })),
  R.values,
);


/**
 * requestSettingItemToOption :: Object -> [Object]
 */
export const requestSettingItemToOption = ({
  _id, title, color, description = '',
}) => ({
  _id, value: title, label: capitalize(title), color, description,
});


/**
 * requestLabelsToOptions :: Object -> [Object]
 */
export const requestLabelsToOptions = labels => labels.map(({
  _id, title, color, description = '',
}) => ({
  _id, value: title, label: capitalize(title), color, description,
}));


/**
 * getOrgSettingId :: (Array | Object, String) -> String
 */
export const getOrgSettingId = (settings, name) =>
  R.type(settings) === 'Array'
    ? R.compose(
      res => res[0]._id,
      R.filter(setting => setting.label === name),
    )(settings)
    : R.compose(
      res => res[0]._id,
      R.filter(setting => setting.title === name),
      R.values(),
    )(settings);


/**
 * filterLabelsByTitle :: (Object, String) -> Object
 */
export const filterLabelsByTitle = (labels, title) => {
  if (labels && Object.keys(labels).length > 0) {
    return Object.keys(labels).reduce((filteredLabels, id) =>
      (labels[id].title !== title)
        ? ({ ...filteredLabels, [id]: labels[id] })
        : filteredLabels, {});
  }
  return labels;
};


/**
 * getSumBudget :: [Request] -> [Number]
 */
export const getSumBudget = requests =>
  R.compose(
    R.uniq,
    R.reduce((acc, { budget }) => {
      if (R.isEmpty(budget) || R.isNil(budget)) {
        return acc;
      }
      if (budget.length === 1) {
        return acc.map(el => el + Number(budget[0]));
      }
      return acc.map((el, index) => el + Number(budget[index]));
    }, [0, 0]),
  )(requests);


/**
 * deleteBudget :: ([Number], [Number]) -> [Number]
 */
export const deleteBudget = (budget, totalBudget) =>
  totalBudget.map((item, index) => {
    if (budget[index]) {
      return item - budget[index];
    }
    if (!R.isEmpty(budget) && !budget[index]) {
      return item - budget[0];
    }
    return item;
  });


/**
 * calculateTotalBudget :: ([Number], [Number], [Number], Boolean) -> Array
 */
export const calculateTotalBudget = (
  newBudget,
  oldBudget = [],
  totalBudget = [],
  removeBudget = false,
) => {
  // remove the old budget from the total budget
  const buffer = deleteBudget(oldBudget, totalBudget);

  if ((!R.isEmpty(oldBudget) && R.isEmpty(newBudget)) || removeBudget) {
    return R.uniq(buffer);
  }

  if (R.equals(newBudget, oldBudget)) {
    return totalBudget;
  }

  if (!R.isNil(newBudget)) {
    // add the new budget to the total budget
    const result = buffer.map((item, index) => {
      if (newBudget[index]) {
        return item + newBudget[index];
      }
      if (!R.isEmpty(newBudget) && !newBudget[index]) {
        return item + newBudget[0];
      }
      if (R.isEmpty(newBudget) && !R.isEmpty(oldBudget)) {
        if (oldBudget[index]) {
          return item + oldBudget[index];
        }
        return item + oldBudget[0];
      }
      return item;
    });

    /* add a budget range if the total budget doesn't contain a range,
      but the new range contains it */
    if (!buffer[1] && newBudget[1] && totalBudget[0] > 0) {
      result.push(buffer[0] ? buffer[0] + newBudget[1] : newBudget[1]);
    }

    return R.uniq(result);
  }

  return totalBudget;
};

/**
 * formatTalentsData :: [Object] -> [Object]
 */
export const formatTalentsData = (talents = []) =>
  talents.map((talent) => {
    const stackInfo = talent.stack && talent.stack.length > 0
      ? talent.stack.reduce((acc, { label }) => {
        if (acc.length > 0) {
          return acc.concat(`, ${label}`);
        }
        return label;
      }, '')
      : '- - -';

    return ({
      ...talent,
      profDirection: talent.profDirection
        ? talent.profDirection.label
        : '- - -',
      stack: stackInfo,
    });
  });



// WEBPACK FOOTER //
// ./src/utils/requests.js